/**
 * The soldier's helmets (DECISIONS 198, 67.56): the light and the heavy, their leader variants, fitted to the soldier's head
 * (soldierLooks.ts `buildHead`: 0.11 wide and 0.12 deep at the eyes, its crown at 0.278).
 *
 * The helmet pass, three rounds on the sheet (the owner, Oct 18: "they still look a little janky"; then "they seem to cover
 * the eyes of all the bots … more one with the ear bulge for some sort of ear equipment … those helmets all sort of like a
 * pith helmet almost"; then "material isn't double sided so the inside of the helmet is see-through. Also I want heavy to be
 * low cut light to be high cut"; his pick: "Let's go with C for helmets"):
 * - modern low-profile shells fitted to the head (`HELMET_FIT`), the brim above the brow;
 * - **light** high-cut, a comms headset's ear cups (`comtac`, ComTac-style domes) under its cutaway, rails, the mount;
 * - **heavy** low-cut, full down over the ears, armoured ear domes, the mandible;
 * - each **lined** (its inside faces turned in), so nothing is see-through from below or behind;
 * - the face under them is option C (soldierLooks.ts `buildHead`): a gaiter up over the nose, the eye line in shade.
 *
 * Every shell is built by `shell`: a bevelled rim with its thickness showing (no stepped lip), the cover's band coloured into
 * the shell, its fittings seated on its curve (side profiles extruded across it, the D guns' style). The drawn helmet's top
 * is ~0.31 over the head's joint; the person's hit capsule keeps its top at the old shell's 0.33 (a little above the drawn
 * helmet, never below: tests/hitShapes; the re-fit is a note under BUILDLIST 65.27). The light helmet is the rifleman's and
 * stays inside the person budget (tests/personModel.test.ts). Rendering only.
 */
import * as THREE from 'three';
import { loft, slab, cbox, piece, bar, type Ring } from './soldierKit';
import { helmetWear, crackShell, type Kit } from './armourLooks';

/** The shells fitted to the head: about 2.5 cm off it all round, the brim above the brow. */
export const HELMET_FIT = {
  light: { rx: 0.135, rz: 0.15, ry: 0.162, hy: 0.15, hz: -0.006 },
  heavy: { rx: 0.142, rz: 0.158, ry: 0.163, hy: 0.15, hz: -0.006 },
};

// ---- the shell ----------------------------------------------------------------------------------------------------------

interface ShellSpec {
  rx: number; rz: number; ry: number; hy: number; hz: number;
  sides: number;
  /** the rings' elevations (radians), the rim's first */
  elev: number[];
  /** the rim's rise by angle round the head (0 = the front): a high cut over the ears, the brow, the nape */
  rim: (a: number) => number;
  colour: string;
  /** the rim's edge and the underside */
  edge: string;
  /** the cover's band: from and to (elevations among `elev`), its colour */
  band?: { from: number; to: number; colour: string };
}
/** A point on the shell's centre line at elevation `e`: its depth ahead (front) and its height. */
const surf = (s: ShellSpec, e: number, front = 1): [number, number] => [s.hz + front * s.rz * Math.cos(e), s.hy + s.ry * Math.sin(e)];
/** The rim's rise at the front (a = 0) and the back (a = π), for fittings placed by elevation there. */
const rimAt = (s: ShellSpec, a: number) => s.rim(a);

/**
 * The shell: a bevelled rim (the underside a little in and up, the edge a little out and down, so the rim shows its
 * thickness without a step), then its rings up to a domed crown, the band coloured in. Triangles: 2·sides a band (the
 * rings + 1 bands), sides − 2 the crown.
 */
function shell(h: THREE.Group, s: ShellSpec): THREE.Mesh {
  const last = s.elev[s.elev.length - 1];
  const ring = (e: number, dr = 0, dy = 0, c?: string): Ring => {
    const w = 1 - e / last;
    return { y: s.hy + Math.sin(e) * s.ry + dy, rx: s.rx * Math.cos(e) + dr, rz: s.rz * Math.cos(e) + dr, z: s.hz, lift: (a: number) => s.rim(a) * w * w, c };
  };
  const rings: Ring[] = [ring(0, -0.009, 0.012, s.edge), ring(0, 0.005, -0.004, s.edge)];
  for (const e of s.elev) rings.push(ring(e, 0, 0, s.band && e >= s.band.from - 1e-9 && e < s.band.to - 1e-9 ? s.band.colour : s.colour));
  const m = loft(h, rings, s.sides, s.colour, false, true);
  m.userData.part = 'shell';
  // its lining: the inside faces turned in, from the rim's underside to a little over the head, capped there — the shell is
  // never see-through from below or behind (the material draws one side; the bake keeps one material)
  loft(h, [ring(0, -0.009, 0.012), ring(0.62, -0.012)], s.sides, s.edge, false, true, true).userData.part = 'lining';
  return m;
}

/** The mount at the front: a side profile seated on the shell's curve between two elevations, standing `out` off it. */
function mount(h: THREE.Group, s: ShellSpec, e0: number, e1: number, out: number, w: number, colour: string, c = 0): void {
  const p = (e: number, o: number) => { const [z, y] = surf(s, e); return [z + o * Math.cos(e), y + o * Math.sin(e) + rimAt(s, 0) * (1 - e / s.elev[s.elev.length - 1]) ** 2]; };
  slab(h, [...p(e0, -0.004), ...p(e1, -0.004), ...p(e1, out * 0.7), ...p((e0 + e1) / 2, out), ...p(e0, out)], w, colour, 0, c);
}
/** The counterweight pouch, seated on the shell's back low down (its battery and strobe). */
function counterweight(k: Kit, h: THREE.Group, s: ShellSpec, colour: string): void {
  const p = (e: number, o: number) => { const [z, y] = surf(s, e, -1); return [z - o * Math.cos(e), y + o * Math.sin(e) + rimAt(s, Math.PI) * (1 - e / s.elev[s.elev.length - 1]) ** 2]; };
  if (k.wear !== 'whole') { const [z, y] = p(0.3, 0.025); piece(k, 'pad', h, 0.12, 0.075, 0.05, colour, 0, y, z, 'back'); return; }
  slab(h, [...p(0.4, -0.004), ...p(0.1, -0.004), ...p(0.12, 0.03), ...p(0.38, 0.026)], 0.1, colour);
}
/** Side rails: a profile across the shell's side at the brow line, a little shorter than the shell is deep there. */
function rails(h: THREE.Group, s: ShellSpec, colour: string): void {
  const y0 = s.hy + 0.012, y1 = s.hy + 0.042, half = s.rz * 0.36;
  for (const sx of [-1, 1]) slab(h, [s.hz - half, y0, s.hz + half, y0, s.hz + half * 0.92, y1, s.hz - half * 0.92, y1], 0.016, colour, sx * (s.rx * 0.965 + 0.004));
}
/**
 * A comms headset's ear cups (ComTac-style electronic ear defenders): rounded domes over the ears, eight-sided so they read
 * round at play distance; `armoured` a larger dome in the heavy plate. At (±x, y) on the head.
 */
export function comtac(h: THREE.Group, colour: string, x = 0.124, y = 0.1, armoured = false): void {
  const r = armoured ? 1.2 : 1;
  for (const sx of [-1, 1]) {
    const cup = loft(h, [{ y: -0.02, rx: 0.046 * r, rz: 0.052 * r }, { y: 0.014 * r, rx: 0.049 * r, rz: 0.055 * r }, { y: 0.036 * r, rx: 0.032 * r, rz: 0.036 * r }], 8, colour, false, true);
    cup.rotation.z = -sx * Math.PI / 2; cup.position.set(sx * x, y, -0.004);
    cup.userData.part = 'cup';
  }
}

// ---- the helmets ------------------------------------------------------------------------------------------------------

/** The rim's rise round the head: `brow` at the front, `nape` at the back (negative: lower), `ears` at the sides. */
const rimOf = (brow: number, nape: number, ears: number) => (a: number) => {
  const f = Math.cos(a), sd = Math.sin(a) * Math.sin(a);
  return (f > 0 ? brow * f : -nape * f) + ears * sd;
};

/** The light helmet: high-cut, the brim above the brow, the cups under the cutaway, rails, the mount, the counterweight. */
function lightHelmet(h: THREE.Group, k: Kit, leader: boolean): ShellSpec {
  const { c } = k, f = HELMET_FIT.light, band = leader ? c.patch : c.soft;
  const s: ShellSpec = { ...f, sides: 9, elev: [0, 0.3, 0.42, 0.85, 1.38], rim: rimOf(0.02, -0.04, 0.035), colour: c.plate, edge: c.trim, band: { from: 0.3, to: 0.42, colour: band } };
  shell(h, s); rails(h, s, c.trim); comtac(h, c.trim, 0.124, 0.1); mount(h, s, 0.45, 0.8, 0.024, 0.065, c.trim); counterweight(k, h, s, c.soft);
  return s;
}

/** The heavy helmet: low-cut, full down over the ears, armoured ear domes over the cups, the mount, the mandible. */
function heavyHelmet(h: THREE.Group, k: Kit, leader: boolean): ShellSpec {
  const { c } = k, f = HELMET_FIT.heavy, band = leader ? c.patch : c.soft;
  const s: ShellSpec = { ...f, sides: 10, elev: [0, 0.3, 0.42, 0.85, 1.38], rim: rimOf(0.02, -0.05, -0.045), colour: c.plate, edge: c.trim, band: { from: 0.3, to: 0.42, colour: band } };
  shell(h, s); rails(h, s, c.trim); counterweight(k, h, s, c.soft); mount(h, s, 0.45, 0.8, 0.024, 0.065, c.trim);
  comtac(h, c.heavyPlate, 0.146, 0.1, true);
  // the mandible guard: a side profile curving round under the chin, extruded across the face, its cheeks bolted on behind
  const z0 = 0.16;
  if (k.wear === 'whole') slab(h, [z0 - 0.04, 0.0, z0 - 0.005, 0.005, z0 + 0.012, 0.04, z0 + 0.004, 0.072, z0 - 0.02, 0.066, z0 - 0.018, 0.04, z0 - 0.04, 0.025], 0.15, c.plate, 0, 0.008);
  else k.plate(h, 0.15, 0.06, 0.035, c.plate, 0, 0.04, z0 - 0.012, 'front');
  for (const sx of [-1, 1]) slab(h, [z0 - 0.11, 0.02, z0 - 0.035, 0.008, z0 - 0.03, 0.068, z0 - 0.1, 0.075], 0.022, c.plate, sx * 0.112, 0.006);
  if (leader) cbox(h, 0.15, 0.012, 0.03, c.patch, 0, 0.006, z0 - 0.02);
  return s;
}

/** A helmet on the head: light or heavy, the leader's variant, its wear. */
export function helmet(h: THREE.Group, k: Kit, heavy: boolean, leader: boolean): void {
  const { c } = k;
  const s = heavy ? heavyHelmet(h, k, leader) : lightHelmet(h, k, leader);
  if (leader) {
    // the leader's variant: the band in the flash (the shell's), a boom mic from the cup, a short antenna at the support
    // side, rank bars
    bar(h, [-0.14, 0.09, 0.03], [-0.06, 0.045, 0.13], 0.014, c.trim);
    cbox(h, 0.028, 0.024, 0.028, c.trim, -0.055, 0.045, 0.133);
    bar(h, [-s.rx + 0.004, s.hy + 0.04, -0.05], [-s.rx - 0.025, s.hy + 0.25, -0.09], 0.013, c.trim);
    for (const dy of [0.02, -0.01]) {   // on the shell's back: its depth at their height
      const y = s.hy + (heavy ? 0.12 : 0.11) + dy, e = Math.asin(Math.min(1, (y - s.hy) / s.ry));
      cbox(h, 0.07, 0.013, 0.012, c.patch, 0, y, s.hz - s.rz * Math.cos(e) - 0.004).userData.part = 'rank';
    }
  }
  // wear: scuffs, or (broken) a split shell (the marks ride the shell's narrower radius)
  if (k.wear === 'worn') helmetWear(k, h, s.rx + 0.004, s.hy);
  if (k.wear === 'broken') crackShell(h, s.rx, s.hy, c, k.rng);
}
